import type { CatalogApi } from "@backstage/catalog-client";
import { parseEntityRef, stringifyEntityRef } from "@backstage/catalog-model";
import type { Entity } from "@backstage/catalog-model";

const STATUS_LABELS: Record<string, string> = {
  open: "Pendiente",
  processing: "En ejecución",
  completed: "Completada",
  failed: "Fallida",
  error: "Fallida",
  cancelled: "Cancelada",
  skipped: "Omitida",
};

/** Display-only label; unknown statuses are shown raw. */
export const statusLabel = (status: string): string =>
  Object.prototype.hasOwnProperty.call(STATUS_LABELS, status)
    ? STATUS_LABELS[status]
    : status;

export type Task = {
  id: string;
  status: string;
  createdAt: string;
  createdBy?: string;
  spec?: {
    parameters?: Record<string, unknown>;
    templateInfo?: {
      entity?: { metadata?: { name?: string; title?: string } };
      entityRef?: string;
    };
  };
};

/**
 * Parámetros que identifican la entidad en los templates locales de catalog/templates/:
 * - edit-saas: saasEntity (referencia al Component existente)
 * - edit-system: systemName (slug del sistema existente)
 * - create-github-team: teamName (nombre del equipo a crear)
 * - update-component-system: componentRef (referencia al Component a actualizar)
 * - update-entity-overlay: entityRef (referencia a la entidad de overlay)
 * - create-saas / create-system: title (en el formulario el usuario introduce title;
 *   el slug técnico saasName/systemName se computa en pasos JSONata posteriores
 *   del wizard y no forma parte de spec.parameters almacenado en la tarea).
 */
const ENTITY_PARAM_KEYS = [
  "saasEntity",
  "systemName",
  "teamName",
  "componentRef",
  "entityRef",
  "title",
] as const;

/** Extrae el nombre de entidad desde una cadena simple o una referencia de entidad de catálogo. */
const extractEntityName = (val: unknown): string | undefined => {
  if (typeof val !== "string") return undefined;
  const trimmed = val.trim();
  if (!trimmed) return undefined;
  try {
    return parseEntityRef(trimmed).name;
  } catch {
    return trimmed;
  }
};

/** Escanea los parámetros del formulario de la tarea buscando el primer identificador de entidad. */
const extractEntityFromParameters = (
  params?: Record<string, unknown>,
): string | undefined => {
  if (!params || typeof params !== "object") return undefined;
  for (const key of ENTITY_PARAM_KEYS) {
    const candidate = extractEntityName(params[key]);
    if (candidate) {
      return candidate;
    }
  }
  return undefined;
};

/**
 * Construye la etiqueta para la columna Tarea: "<Plantilla> (<Entidad>)" o fallbacks seguros.
 * Nunca muestra el UUID de la tarea como etiqueta visible.
 */
export const taskDisplayLabel = (task: Task): string => {
  const templateTitle =
    task.spec?.templateInfo?.entity?.metadata?.title ??
    task.spec?.templateInfo?.entity?.metadata?.name ??
    (task.spec?.templateInfo?.entityRef
      ? extractEntityName(task.spec.templateInfo.entityRef)
      : undefined);

  const entityName = extractEntityFromParameters(task.spec?.parameters);

  if (templateTitle && entityName) {
    return `${templateTitle} (${entityName})`;
  }
  if (templateTitle) {
    return templateTitle;
  }
  if (entityName) {
    return `Tarea (${entityName})`;
  }
  return "Tarea";
};

/** Normaliza createdBy hacia una EntityRef de usuario válida para EntityRefLink. */
export const toUserEntityRef = (createdBy?: string): string | undefined => {
  if (!createdBy || !createdBy.trim()) return undefined;
  try {
    const parsed = parseEntityRef(createdBy.trim(), {
      defaultKind: "user",
      defaultNamespace: "default",
    });
    return stringifyEntityRef(parsed);
  } catch {
    return undefined;
  }
};

export type CatalogGroupClient = Pick<CatalogApi, "queryEntities">;

export const fetchCatalogGroups = async (
  catalog: CatalogGroupClient,
  fields: string[],
  limit = 200,
): Promise<Entity[]> => {
  const all: Entity[] = [];
  let cursor: string | undefined;

  for (;;) {
    const page = await catalog.queryEntities(
      cursor
        ? { cursor, fields, limit }
        : { filter: { kind: "Group" }, fields, limit },
    );

    all.push(...page.items);

    const nextCursor = page.pageInfo?.nextCursor;
    if (!nextCursor) {
      return all;
    }

    cursor = nextCursor;
  }
};

export type TaskQuery = {
  /** undefined = no createdBy filter (all authorized tasks) */
  createdBy?: string;
  createdByGroup?: string;
  limit: number;
  offset: number;
};

/** Builds the /v2/tasks query with server-side filtering and pagination. */
export const buildTasksUrl = (baseUrl: string, q: TaskQuery): string => {
  const p = new URLSearchParams();
  if (q.createdBy) p.set("createdBy", q.createdBy);
  if (q.createdByGroup) p.set("createdByGroup", q.createdByGroup);
  p.set("limit", String(q.limit));
  p.set("offset", String(q.offset));
  p.set("order", "desc:created_at");
  return `${baseUrl}/v2/tasks?${p}`;
};
